// Pure helpers for the insurance expiration issues' decide flow (Joy
// 2026-07-01). The ins-missing-expiration recommendation says "confirm
// the date on the carrier's site and set it" — but the rail's "verify"
// was a pure disposition with nowhere to type the date, so Joy asked
// "how do I update the expiration?". The rail now renders a date input
// and sends params.expiration_date; the backend writes it onto the
// policy (audited, 60s-undoable) and anchors a Renewal Plan cycle.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/ —
// same convention as household-cluster-predicate.ts / record-links.ts.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface ExpirationIssueLike {
  detectorSlug: string;
}

// Issues whose "verify" carries an expiration date to the backend.
export function isExpirationIssue(issue: ExpirationIssueLike): boolean {
  return (
    issue.detectorSlug === "ins-missing-expiration" ||
    issue.detectorSlug === "ins-stale-active"
  );
}

// ins-missing-expiration: entering the date IS the action (backend
// 400s without it). ins-stale-active: optional — plain verify means
// "confirmed still in force, the date on file is right".
export function expirationDateRequired(issue: ExpirationIssueLike): boolean {
  return issue.detectorSlug === "ins-missing-expiration";
}

// Mirror of shared/lib/iso-date.ts (kept dependency-free so this module
// stays root-safe): strict YYYY-MM-DD that round-trips through Date.
export function isValidExpirationInput(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

// Plain-language before→after for the expiration-carrying verify (UX
// rule #3) — replaces the generic "Marks this verified" copy when a
// date is in play.
export function expirationConsequence(
  issue: ExpirationIssueLike,
  date: string,
): string | null {
  if (!isExpirationIssue(issue)) return null;
  if (date && isValidExpirationInput(date)) {
    return `Sets the policy's expiration to ${date} (as confirmed at the carrier) and resolves this issue. If the policy has no active Renewal Plan cycle, one is created anchored on that date so the renewal can schedule. Audited; reversible for 60 seconds.`;
  }
  if (expirationDateRequired(issue)) {
    return "Enter the expiration date from the carrier's site first — setting it is the whole action here. The Renewal Plan can't schedule without it.";
  }
  return null; // stale-active with no date → the generic verify copy.
}
